import type { CSSProperties, ReactNode } from "react";
import { useEffect, useRef } from "react";
import { cn } from "../lib/utils";

interface InfiniteScrollProps {
  onLoadMore: () => void;
  hasMore: boolean;
  loading?: boolean;
  threshold?: number;
  loader?: ReactNode;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}

export function InfiniteScroll({
  onLoadMore,
  hasMore,
  loading = false,
  threshold = 0,
  loader,
  children,
  className,
  style,
}: InfiniteScrollProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(loading);
  loadingRef.current = loading;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingRef.current) {
          onLoadMore();
        }
      },
      { rootMargin: `${threshold}px` }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [onLoadMore, threshold]);

  return (
    <div className={cn("infinite-scroll", className)} style={style}>
      {children}
      {hasMore && (
        <div ref={sentinelRef} className="infinite-scroll-sentinel">
          {loading && (
            loader ?? (
              <div className="infinite-scroll-loader">
                <span className="infinite-scroll-dot" />
                <span className="infinite-scroll-dot" />
                <span className="infinite-scroll-dot" />
              </div>
            )
          )}
        </div>
      )}
      {!hasMore && (
        <div className="infinite-scroll-end">End of results</div>
      )}
    </div>
  );
}
